import { Pipe, PipeTransform } from '@angular/core';
import { BadgeQuestionMark, Building2, Factory, Home, LucideIconData } from 'lucide-angular';

const PROJECT_TYPE_ICON_PATHS = {
  residential: 'assets/icons/project-types/residential.png',
  commercial: 'assets/icons/project-types/commercial.png',
  industrial: 'assets/icons/project-types/industrial.png',
  other: 'assets/icons/project-types/other.png',
} as const;

function projectTypeKey(type: ProjectType | null | undefined): string {
  return (type ?? '').trim().toLowerCase();
}

/** Project type values supported by badge styling, with fallback strings allowed. */
export type ProjectType = 'Residential' | 'Commercial' | 'Industrial' | 'Other' | string;

/**
 * Maps a project type to the Tailwind classes used by project-type badges.
 */
@Pipe({ name: 'projectTypeBadge', standalone: true })
export class ProjectTypeBadgePipe implements PipeTransform {
  /**
   * Returns the badge classes for a project type.
   *
   * @param type Project type label, or empty when not yet selected.
   * @returns Tailwind class string for the project-type badge.
   */
  transform(type: ProjectType | null | undefined): string {
    switch (projectTypeKey(type)) {
      case 'residential': return 'bg-talos-accent/6 text-talos-accent outline outline-1 outline-talos-accent/18';
      case 'commercial':  return 'bg-talos-bg text-talos-ink/72 outline outline-1 outline-talos-ink/18';
      case 'industrial':  return 'bg-talos-accent/12 text-talos-accent outline outline-1 outline-talos-accent/30';
      case 'other':       return 'bg-talos-ink/4 text-talos-ink/64 outline outline-1 outline-talos-accent/22';
      default:            return 'bg-talos-bg text-talos-ink/56 outline outline-1 outline-talos-ink/14';
    }
  }
}

/**
 * Maps a project type to its local Flaticon asset.
 */
@Pipe({ name: 'projectTypeIconSrc', standalone: true })
export class ProjectTypeIconSrcPipe implements PipeTransform {
  /**
   * Returns the local image asset path for a project type.
   *
   * @param type Project type label, or empty when not yet selected.
   */
  transform(type: ProjectType | null | undefined): string {
    switch (projectTypeKey(type)) {
      case 'residential': return PROJECT_TYPE_ICON_PATHS.residential;
      case 'commercial':  return PROJECT_TYPE_ICON_PATHS.commercial;
      case 'industrial':  return PROJECT_TYPE_ICON_PATHS.industrial;
      case 'other':       return PROJECT_TYPE_ICON_PATHS.other;
      default:            return PROJECT_TYPE_ICON_PATHS.other;
    }
  }
}

/**
 * Maps a project type to its badge icon.
 */
@Pipe({ name: 'projectTypeBadgeIcon', standalone: true })
export class ProjectTypeBadgeIconPipe implements PipeTransform {
  /**
   * Returns the Lucide icon for a project type.
   *
   * @param type Project type label, or empty when not yet selected.
   */
  transform(type: ProjectType | null | undefined): LucideIconData {
    switch (projectTypeKey(type)) {
      case 'residential': return Home;
      case 'commercial':  return Building2;
      case 'industrial':  return Factory;
      case 'other':       return BadgeQuestionMark;
      default:            return BadgeQuestionMark;
    }
  }
}
